import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ISpirometrySession,
  ISpirometrySessionSortBy,
} from "../../../@types/admin/AdminSpirometry";
import { TableSorted } from "../../../components/TableSorted";
import {
  SPIROMETRY_SESSIONS_QUERY_KEY,
  useSpirometrySessions,
} from "../../../hooks/queries/admin/spirometry";
import { useDebounce } from "../../../hooks/useDebounce";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
} from "../shared";

const DEFAULT_PAGE_SIZE = 25;

/**
 * A placeholder from the 2023 pre-ArtiQ backfill (populate-old-blows.sql):
 * empty version, analysed_on = the backfill time, best values stored as 0.
 * 42% of production sessions; their "analysis" fields are not data.
 */
export function isBackfilled(session: ISpirometrySession) {
  return !session.version;
}

export function formatTwoDecimals(value: number | null) {
  return value == null ? "-" : value.toFixed(2);
}

/**
 * Column id → the API's sort key, as on the admin Patients list. Sorting runs
 * on the server (`manualSorting`): this list is paged, so sorting in the
 * browser would reorder only the rows on screen. Patient and Blows are
 * resolved per row, so they are not sortable.
 */
const SORT_KEYS: Record<string, ISpirometrySessionSortBy> = {
  id: "Id",
  analysedOn: "AnalysedOn",
  overallGrade: "OverallGrade",
  bestFev1: "BestFev1",
  bestFvc: "BestFvc",
  sessionId: "SessionId",
  version: "Version",
};

const DEFAULT_SORTING: SortingState = [{ id: "id", desc: true }];

// Every column has an accessorFn returning text: TableSorted dashes falsy values.
const COLUMNS: ColumnDef<ISpirometrySession, string>[] = [
  {
    id: "id",
    header: "ID",
    accessorFn: (session) => String(session.id),
    enableSorting: true,
  },
  {
    id: "analysedOn",
    header: "Analysed on",
    accessorFn: (session) =>
      isBackfilled(session)
        ? "Not analysed"
        : getLongFormattedDate(session.analysedOn, "-"),
    enableSorting: true,
  },
  {
    id: "patient",
    header: "Patient",
    accessorFn: (session) =>
      session.patientId == null
        ? "-"
        : `${session.patientName || "Unnamed"} (${session.patientId})`,
    enableSorting: false,
  },
  {
    id: "overallGrade",
    header: "Overall grade",
    accessorFn: (session) => session.overallGrade ?? "-",
    enableSorting: true,
  },
  {
    id: "grades",
    header: "FEV1 / FVC grade",
    accessorFn: (session) =>
      `${session.fev1Grade || "-"} / ${session.fvcGrade || "-"}`,
    enableSorting: false,
  },
  {
    id: "bestFev1",
    header: "Best FEV1 (L)",
    accessorFn: (session) =>
      isBackfilled(session) ? "-" : formatTwoDecimals(session.bestTrialFev1),
    enableSorting: true,
  },
  {
    id: "bestFvc",
    header: "Best FVC (L)",
    accessorFn: (session) =>
      isBackfilled(session) ? "-" : formatTwoDecimals(session.bestTrialFvc),
    enableSorting: true,
  },
  {
    id: "trialCount",
    header: "Blows",
    accessorFn: (session) => String(session.trialCount),
    enableSorting: false,
  },
  {
    id: "sessionId",
    header: "Session ID",
    accessorFn: (session) => session.sessionId ?? "-",
    enableSorting: true,
  },
  {
    id: "version",
    header: "API version",
    accessorFn: (session) => session.version || "-",
    enableSorting: true,
  },
];

/**
 * Spirometry Sessions admin page (EN-4125), replacing the CMS "Spirometry
 * test assessment sessions" list. Read-only: the ticket needs the data
 * visible, not editable. A row opens the session with its blows.
 */
export function AdminSpirometrySessions() {
  document.title = "Spirometry sessions - Admin";

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 400);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const sort = sorting[0] ?? DEFAULT_SORTING[0];

  const { sessions, totalCount, isFetching, isError } = useSpirometrySessions({
    page,
    pageSize,
    search: debouncedSearch,
    sortBy: SORT_KEYS[sort.id] ?? "Id",
    sortDescending: sort.desc,
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pagination = {
    currentPage: page,
    pageSize,
    totalPages,
    totalItems: totalCount,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Spirometry sessions</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by patient name, patient ID, session ID or row ID..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the spirometry sessions. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper
          refreshKeys={[[SPIROMETRY_SESSIONS_QUERY_KEY]]}
          isFetching={isFetching && sessions.length > 0}
        >
          <TableSorted
            columns={COLUMNS}
            data={sessions}
            onRowClick={(session: ISpirometrySession) =>
              navigate(`/admin/patients/spirometry/${session.id}`)
            }
            emptyStateText={
              isFetching ? <TableLoadingState /> : "No rows found"
            }
            sorting={sorting}
            onSortingChange={(next: SortingState) => {
              // A third click clears the sort; fall back to newest first.
              setSorting(next.length > 0 ? next : DEFAULT_SORTING);
              setPage(1);
            }}
            manualSorting
          />
        </TableWrapper>
        <TablePagination
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          exportCsv={{
            filename: "spirometry-sessions",
            columns: COLUMNS,
            rows: sessions,
            currentPageOnly: true,
          }}
        />
      </TableContainer>
    </PageContainer>
  );
}
